import { useCallback } from "react";
import { useSelector, useStore } from "react-redux";

import { getDisplayName } from "./getDisplayName";

// This hook allows components to track the sticky elements (alert banner, main navigation
// header and current page's affix header) height, which allows them to render their
// sticky elements below the main headers.
//
export const useStickyElementsHeight = (options = {}) => {
  const pushableContainersOffset = useSelector(
    ({ styling }) => styling.pushableContainersOffset || 0
  );
  const affixHeaderOffsetTop = useSelector(
    ({ styling }) => styling.affixHeaderOffsetTop || 0
  );
  const stickyTitleBarHeight = useSelector(
    ({ styling }) => styling.stickyTitleBarHeight || 0
  );

  return getStickyElementsHeight({
    pushableContainersOffset,
    affixHeaderOffsetTop,
    stickyTitleBarHeight,
    options
  });
};

// This returns a function that can be called to get the height of all sticky elements at
// the moment it is called. It's useful for components that need to calculate their
// sticky position offset at a given point in time, and don't care about the value to
// change.
//
// For options arguments, see the `getStickyElementsHeight` private function code below.
//
// If the value should be updated when the sticky elements height changes, the
// `useStickyElementsHeight` hook should be preferred as the component is re-rendered
// whenever one of the sub-values changes.
//
export const useGetStickyElementsHeight = ignoredStickyElements => {
  const store = useStore();

  return useCallback(
    (options = ignoredStickyElements) => {
      const { styling } = store.getState();
      const pushableContainersOffset = styling.pushableContainersOffset || 0;
      const affixHeaderOffsetTop = styling.affixHeaderOffsetTop || 0;
      const stickyTitleBarHeight = styling.stickyTitleBarHeight || 0;

      return getStickyElementsHeight({
        pushableContainersOffset,
        affixHeaderOffsetTop,
        stickyTitleBarHeight,
        options
      });
    },
    [ignoredStickyElements, store]
  );
};

// Internal helper function used by both above hooks to calculate the height of the sticky
// elements that are currently rendered in the layout, and should be taken into account
// when rendering other sticky elements that must be displayed below the header
// components.
//
const getStickyElementsHeight = ({
  pushableContainersOffset,
  affixHeaderOffsetTop,
  stickyTitleBarHeight,
  options
}) => {
  const { ignoreTopNavBar, ignoreStickyTitleBar, ignoreAlert } = options || {};

  let height = 0;

  if (!ignoreTopNavBar) height = height + affixHeaderOffsetTop;
  if (!ignoreStickyTitleBar) height = height + stickyTitleBarHeight;
  if (!ignoreAlert) height = height + pushableContainersOffset;

  return height;
};

// This is needed for the `withMapPanelSizing` HOC.
//
// TODO : Refactor the `withMapPanelSizing` HOC to a hook and remove this HOC too.
//
export const withStickyElementsHeight = WrappedComponent => {
  const WithStickyElementsHeight = props => {
    const getStickyElementsHeight = useGetStickyElementsHeight();

    return (
      <WrappedComponent {...props} getStickyElementsHeight={getStickyElementsHeight} />
    );
  };

  WithStickyElementsHeight.displayName = `WithStickyElementsHeight(${getDisplayName(
    WrappedComponent
  )})`;

  return WithStickyElementsHeight;
};
